import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  FILM_TIERS,
  hasFeatureAccess,
  hasMinimumTier,
  getEffectiveTier,
  isSubscriptionActive
} from '../lib/filmSubscriptionTiers';

/**
 * Hook for checking Film subscription access
 *
 * @returns {Object} Subscription data and access check functions
 */
export const useFilmSubscription = () => {
  const { userProfile, isSuperAdmin } = useAuth();

  // Extract subscription data from user profile
  const subscriptionData = useMemo(() => {
    const forceUnlock = isSuperAdmin();

    if (!userProfile) {
      return {
        tier: FILM_TIERS.FREE,
        status: null,
        expiresAt: null,
        startedAt: null,
        cancelAtPeriodEnd: false,
        companyId: null,
        isEnterpriseAdmin: false,
        forceUnlock,
      };
    }

    return {
      tier: userProfile.filmSubscriptionTier || FILM_TIERS.FREE,
      status: userProfile.filmSubscriptionStatus || null,
      expiresAt: userProfile.filmSubscriptionExpiresAt || null,
      startedAt: userProfile.filmSubscriptionStartedAt || null,
      cancelAtPeriodEnd: Boolean(userProfile.filmSubscriptionCancelAtPeriodEnd),
      companyId: userProfile.enterpriseCompanyId || userProfile.filmCompanyId || null,
      isEnterpriseAdmin: userProfile.isEnterpriseAdmin || userProfile.isFilmEnterpriseAdmin || false,
      forceUnlock,
    };
  }, [userProfile, isSuperAdmin]);

  // Calculate effective tier (considering active status)
  const effectiveTier = useMemo(() => {
    if (subscriptionData.forceUnlock) {
      return FILM_TIERS.ENTERPRISE;
    }

    return getEffectiveTier(
      subscriptionData.tier,
      subscriptionData.status,
      subscriptionData.expiresAt
    );
  }, [
    subscriptionData.forceUnlock,
    subscriptionData.tier,
    subscriptionData.status,
    subscriptionData.expiresAt,
  ]);

  // Check if subscription is active
  const isActive = useMemo(() => {
    if (subscriptionData.forceUnlock) {
      return true;
    }

    return isSubscriptionActive(
      subscriptionData.status,
      subscriptionData.expiresAt
    );
  }, [
    subscriptionData.forceUnlock,
    subscriptionData.status,
    subscriptionData.expiresAt,
  ]);

  // Check if user is enterprise member
  const isEnterpriseMember = useMemo(() => {
    return Boolean(subscriptionData.companyId);
  }, [subscriptionData.companyId]);

  /**
   * Check if user has access to a specific feature
   * @param {string} feature - Feature name (homeWidget, dashboard, filmDetail, deepDive)
   * @returns {boolean}
   */
  const hasFilmAccess = (feature) => {
    return hasFeatureAccess(effectiveTier, feature);
  };

  /**
   * Check if user has at least the specified tier
   * @param {string} requiredTier - Minimum tier required
   * @returns {boolean}
   */
  const hasFilmTier = (requiredTier) => {
    return hasMinimumTier(effectiveTier, requiredTier);
  };

  // Derived booleans for common feature checks
  const canAccessDashboard = useMemo(() => hasFeatureAccess(effectiveTier, 'dashboard'), [effectiveTier]);
  const canAccessFilmDetail = useMemo(() => hasFeatureAccess(effectiveTier, 'filmDetail'), [effectiveTier]);
  const canAccessDeepDive = useMemo(() => hasFeatureAccess(effectiveTier, 'deepDive'), [effectiveTier]);

  return {
    // Subscription data
    filmTier: effectiveTier,
    filmStatus: subscriptionData.status,
    filmExpiresAt: subscriptionData.expiresAt,
    filmStartedAt: subscriptionData.startedAt,
    filmCancelsAtPeriodEnd: subscriptionData.cancelAtPeriodEnd,
    forceUnlocked: subscriptionData.forceUnlock,
    enterpriseCompanyId: subscriptionData.companyId,
    filmCompanyId: subscriptionData.companyId, // legacy alias
    isEnterpriseAdmin: subscriptionData.isEnterpriseAdmin,
    isFilmEnterpriseAdmin: subscriptionData.isEnterpriseAdmin,

    // Status checks
    isActive,
    isEnterpriseMember,

    // Access check helpers
    hasFilmAccess,
    hasFilmTier,
    canAccessDashboard,
    canAccessFilmDetail,
    canAccessDeepDive
  };
};

export default useFilmSubscription;

